// Set-password (invite accept) page — self-serve first login (2026-07-20).
//
// The invitation email links here: /auth/set-password?token=RAW. On mount we
// GET /auth/set-password?token= (public, skipAuth) to validate the token and
// read the account email. Invalid/expired → a friendly full-screen state with
// no form. Valid → show the email (read-only) + password + confirm fields
// (min 12 chars). Submitting POSTs /auth/set-password, which auto-logins:
// it returns the access token + user and sets the refresh cookie, so we
// adopt the session and land the user straight in the app.
//
// Editorial layout mirrors LoginPage: split brand panel on the left, form on
// the right, same Input/Button primitives + brand palette.
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FlowLockup } from "@/components/FlowLockup";
import { twoStepFromBody, useAuth, type SessionUser } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";

const MIN_PASSWORD = 12;

function BrandPanel() {
  return (
    <div className="relative hidden bg-brown-500 p-12 text-cream-200 md:flex md:flex-col md:justify-between">
      <FlowLockup width={112} className="self-start text-white" />
      <div>
        <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight text-cream-200">
          Welcome
          <br />
          to other.
        </h1>
        <p className="mt-6 max-w-md text-base text-cream-300/80">
          Set your password to activate your account. One workflow for
          sales, production, and clients.
        </p>
      </div>
      <div className="text-xs text-cream-200/60">
        © {new Date().getFullYear()} other · Boca Raton, FL
      </div>
    </div>
  );
}

export function SetPasswordPage() {
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [status, setStatus] = useState<"loading" | "valid" | "invalid">(
    "loading",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const body = await api<{ email: string; expires_at: string }>(
          `/auth/set-password?token=${encodeURIComponent(token)}`,
          { skipAuth: true },
        );
        if (!cancelled) {
          setEmail(body.email);
          setStatus("valid");
        }
      } catch {
        // 404 invitation_not_found_or_expired (or any failure) → no form.
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    password.length >= MIN_PASSWORD && confirm === password && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const body = await api<{ access_token: string; user: SessionUser }>(
        "/auth/set-password",
        {
          method: "POST",
          body: JSON.stringify({ token, password }),
          skipAuth: true,
        },
      );
      // Two-step sign-in (2026-10-21): when the server answers mfa_required
      // the new teammate sets up their authenticator app on the sign-in page
      // before their first session.
      const twoStep = twoStepFromBody(body);
      if (twoStep) {
        navigate("/login", { replace: true, state: { twoStep } });
        return;
      }
      // Auto-login: the server minted the session + set the refresh cookie.
      adoptSession(body.access_token, body.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setStatus("invalid");
        } else if (err.status === 400) {
          setError(
            `Password must be at least ${MIN_PASSWORD} characters.`,
          );
        } else {
          setError("Something went wrong. Please try again in a moment.");
        }
      } else {
        setError("Network error. Check your connection and try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <BrandPanel />

      <div className="flex items-center justify-center bg-cream-50 px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 md:hidden">
            <FlowLockup width={96} className="text-ink" />
          </div>

          {status === "loading" && (
            <p className="text-sm text-brown-300">Checking your invitation…</p>
          )}

          {status === "invalid" && (
            <div>
              <h2 className="font-display text-3xl font-bold text-brown-500">
                This link isn't active
              </h2>
              <p className="mt-3 text-sm text-brown-400">
                This invitation link has expired or was already used. Ask
                your admin to send a fresh one.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm font-semibold text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          )}

          {status === "valid" && (
            <>
              <h2 className="font-display text-3xl font-bold text-brown-500">
                Set your password
              </h2>
              <p className="mt-2 text-sm text-brown-400">
                Activate your other account.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                <div>
                  <label
                    htmlFor="account-email"
                    className="mb-1.5 block text-sm font-semibold text-brown-500"
                  >
                    Account
                  </label>
                  <Input
                    id="account-email"
                    type="email"
                    value={email}
                    readOnly
                    disabled
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-semibold text-brown-500"
                  >
                    New password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 12 characters"
                  />
                  {tooShort && (
                    <p className="mt-1.5 text-xs text-rust-600">
                      Must be at least {MIN_PASSWORD} characters.
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="confirm"
                    className="mb-1.5 block text-sm font-semibold text-brown-500"
                  >
                    Confirm password
                  </label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                  {mismatch && (
                    <p className="mt-1.5 text-xs text-rust-600">
                      Passwords don't match.
                    </p>
                  )}
                </div>

                {error && (
                  <div
                    role="alert"
                    className="rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="destructive"
                  size="lg"
                  className="w-full"
                  disabled={!canSubmit}
                >
                  {submitting ? "Setting password…" : "Set password & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
